import React, { useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withStyles } from 'tss-react/mui';
import VimeoVideo from 'src/components/VimeoVideo/index';
import GoogleDoc from 'src/components/GoogleDoc/index';
import H4Title from 'src/components/HTitleTags/H4Title';
import { STYLE_OPTIONS } from 'src/constants';
import helpSectionsData from './helpSectionsData';
import { getCompanyOverrides } from './companyOverrides';

const styles = {
  accordion: {
    marginBottom: '8px',
    fontFamily: STYLE_OPTIONS.FONT_FAMILY,
    borderLeft: '4px solid rgba(102, 126, 234, 0.3)',
    transition: 'border-left-color 0.2s ease',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 8px 0',
      borderLeftColor: STYLE_OPTIONS.COLOR_BLUE,
    },
    '&:hover': {
      borderLeftColor: 'rgba(102, 126, 234, 0.6)',
    },
  },
  accordionSummary: {
    backgroundColor: 'rgba(102, 126, 234, 0.08)',
    borderRadius: '4px',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginRight: '12px',
      marginLeft: '0',
      color: STYLE_OPTIONS.COLOR_BLUE,
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(180deg)',
    },
    '&.Mui-expanded': {
      borderRadius: '4px 4px 0 0',
    },
  },
  accordionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: STYLE_OPTIONS.COLOR_BLUE,
    fontFamily: STYLE_OPTIONS.FONT_FAMILY,
  },
  accordionDetails: {
    padding: '16px 24px',
    display: 'block',
  },
  // Nested accordion styles
  nestedAccordion: {
    marginBottom: '4px',
    boxShadow: 'none',
    border: '1px solid rgba(102, 126, 234, 0.15)',
    borderLeft: '3px solid rgba(102, 126, 234, 0.3)',
    transition: 'border-left-color 0.2s ease',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 4px 0',
      borderLeftColor: STYLE_OPTIONS.COLOR_BLUE,
    },
    '&:hover': {
      borderLeftColor: 'rgba(102, 126, 234, 0.6)',
      backgroundColor: 'rgba(102, 126, 234, 0.02)',
    },
  },
  nestedAccordionSummary: {
    backgroundColor: 'transparent',
    minHeight: '44px',
    flexDirection: 'row-reverse',
    padding: '0 12px',
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginRight: '8px',
      marginLeft: '0',
      color: STYLE_OPTIONS.COLOR_BLUE,
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(180deg)',
    },
    '&.Mui-expanded': {
      minHeight: '44px',
    },
  },
  nestedAccordionTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: STYLE_OPTIONS.COLOR_TEXT,
    fontFamily: STYLE_OPTIONS.FONT_FAMILY,
  },
  nestedAccordionDetails: {
    padding: '16px',
    display: 'block',
    backgroundColor: '#fafafa',
  },
  subtitle: {
    marginBottom: '16px',
  },
  videoContainer: {
    marginBottom: '16px',
  },
  documentContainer: {
    marginTop: '8px',
    marginBottom: '8px',
  },
  documentLink: {
    ...STYLE_OPTIONS.A_PRIMARY,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  singleItemContainer: {
    padding: '8px 0',
  },
};

/**
 * Merges base section data with company-specific overrides
 */
const getMergedSections = (company, textingProvider) => {
  const overrides = getCompanyOverrides(company);

  // cpOnly sections keep their pre-demotion CP-texting-only visibility
  const visibleSections = helpSectionsData.filter(
    (section) => !section.cpOnly || textingProvider === 'CP',
  );

  const mergedSections = visibleSections.map((section) => {
    const sectionOverride = overrides[section.id];
    if (!sectionOverride) return section;

    const mergedSection = { ...section };

    // Handle item overrides (replace specific items by title)
    if (sectionOverride.itemOverrides) {
      mergedSection.items = section.items.map((item) => {
        const override = sectionOverride.itemOverrides[item.title];
        if (override) {
          return { ...item, ...override };
        }
        return item;
      });
    }

    // Handle additional items (append to end)
    if (sectionOverride.additionalItems) {
      mergedSection.items = [
        ...(mergedSection.items || section.items),
        ...sectionOverride.additionalItems,
      ];
    }

    return mergedSection;
  });

  // Add company-specific new sections (only for CP texting users)
  if (textingProvider === 'CP') {
    Object.keys(overrides).forEach((sectionId) => {
      const override = overrides[sectionId];
      if (override.section) {
        mergedSections.push(override.section);
      }
    });
  }

  // A section can end up with no items (e.g. its only remaining
  // content is a company override the current user doesn't get);
  // hide it rather than render an empty accordion.
  return mergedSections.filter((section) => section.items && section.items.length > 0);
};

/**
 * Renders video content
 */
function VideoContent({ item, classes }) {
  return (
    <Box className={classes.videoContainer}>
      <VimeoVideo
        link={`https://player.vimeo.com/video/${item.vimeoId}`}
        positionsY="left"
      />
    </Box>
  );
}

VideoContent.propTypes = {
  item: PropTypes.shape({
    vimeoId: PropTypes.string.isRequired,
  }).isRequired,
  classes: PropTypes.object.isRequired,
};

/**
 * Renders document content
 */
function DocumentContent({ item, classes }) {
  return (
    <Box className={classes.documentContainer}>
      <p>
        <a
          target="_blank"
          href={item.linkUrl}
          rel="noopener noreferrer"
          className={classes.documentLink}
        >
          {item.linkText}
        </a>
        &nbsp;Click on the link to open in a different tab, or scroll through below.
      </p>
      <GoogleDoc link={item.embedUrl} />
    </Box>
  );
}

DocumentContent.propTypes = {
  item: PropTypes.shape({
    linkText: PropTypes.string.isRequired,
    linkUrl: PropTypes.string.isRequired,
    embedUrl: PropTypes.string.isRequired,
  }).isRequired,
  classes: PropTypes.object.isRequired,
};

/**
 * Renders a single item without accordion (for sections with only 1 item)
 */
function SingleItemContent({ item, classes }) {
  return (
    <Box className={classes.singleItemContainer}>
      {item.type === 'video' && <VideoContent item={item} classes={classes} />}
      {item.type === 'document' && <DocumentContent item={item} classes={classes} />}
    </Box>
  );
}

SingleItemContent.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string.isRequired,
  }).isRequired,
  classes: PropTypes.object.isRequired,
};

/**
 * Renders nested accordions for sections with 2+ items
 */
function NestedAccordions(
  {
    items,
    sectionId,
    classes,
    expandedNested,
    onNestedChange
  }
) {
  return (
    <Box>
      {items.map((item) => {
        const itemKey = `${sectionId}-${item.vimeoId || item.linkUrl}`;
        return (
          <Accordion
            key={itemKey}
            expanded={expandedNested === itemKey}
            onChange={onNestedChange(itemKey)}
            className={classes.nestedAccordion}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.nestedAccordionSummary}
            >
              <Typography className={classes.nestedAccordionTitle}>
                {item.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.nestedAccordionDetails}>
              {item.type === 'video' && <VideoContent item={item} classes={classes} />}
              {item.type === 'document' && <DocumentContent item={item} classes={classes} />}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

NestedAccordions.propTypes = {
  items: PropTypes.array.isRequired,
  sectionId: PropTypes.number.isRequired,
  classes: PropTypes.object.isRequired,
  expandedNested: PropTypes.string,
  onNestedChange: PropTypes.func.isRequired,
};

NestedAccordions.defaultProps = {
  expandedNested: null,
};

/**
 * Main accordion component for help sections
 */
const HelpSectionAccordion = forwardRef((
  { primaryCompany, textingProvider, classes },
  ref
) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedNested, setExpandedNested] = useState(null);
  const [sectionRefs] = useState({});

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    // Reset nested accordion when main section changes
    if (!isExpanded) {
      setExpandedNested(null);
    }
  };

  const handleNestedChange = (panel) => (event, isExpanded) => {
    setExpandedNested(isExpanded ? panel : null);
  };

  // Expose method to expand and scroll to a specific section
  useImperativeHandle(ref, () => ({
    expandAndScrollToSection: (sectionId) => {
      setExpanded(sectionId);
      // Wait for accordion to expand before scrolling
      setTimeout(() => {
        const sectionElement = sectionRefs[sectionId];
        if (sectionElement) {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    },
  }));

  const sections = getMergedSections(primaryCompany, textingProvider);

  return (
    <Box>
      {sections.map((section, sectionIndex) => (
        <Accordion
          key={section.id}
          expanded={expanded === section.id}
          onChange={handleChange(section.id)}
          className={classes.accordion}
          ref={(el) => { sectionRefs[section.id] = el; }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <Typography className={classes.accordionTitle}>
              {`${sectionIndex + 1}. ${section.title}`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {section.subtitle && (
              <H4Title className={classes.subtitle}>
                {section.subtitle}
              </H4Title>
            )}
            {section.items.length === 1 ? (
              <SingleItemContent
                item={section.items[0]}
                classes={classes}
              />
            ) : (
              <NestedAccordions
                items={section.items}
                sectionId={section.id}
                classes={classes}
                expandedNested={expandedNested}
                onNestedChange={handleNestedChange}
              />
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
});

HelpSectionAccordion.propTypes = {
  primaryCompany: PropTypes.string,
  textingProvider: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

HelpSectionAccordion.defaultProps = {
  primaryCompany: null,
  textingProvider: null,
};

export default withStyles(HelpSectionAccordion, styles);
